import React from 'react';
import i18n from '../i18n/config';

export interface PageErrorBoundaryProps {
  children: React.ReactNode;
  /** Called when user clicks "Try again". Parent can remount by changing key. */
  onRetry?: () => void;
  /** Optional custom message. */
  message?: string;
}

interface PageErrorBoundaryState {
  hasError: boolean;
}

/**
 * Catches render errors in a page subtree. Shows inline fallback so the app-level
 * error screen is not shown. Use onRetry to remount (e.g. setKey(k => k + 1)) for a fresh load.
 */
export class PageErrorBoundary extends React.Component<PageErrorBoundaryProps, PageErrorBoundaryState> {
  state: PageErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): PageErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    console.error('PageErrorBoundary', error, info.componentStack);
  }

  handleRetry = (): void => {
    this.setState({ hasError: false });
    this.props.onRetry?.();
  };

  render(): React.ReactNode {
    if (this.state.hasError) {
      return (
        <div
          className="flex min-h-[280px] flex-col items-center justify-center gap-4 rounded-card border border-surface-border bg-surface-cards p-8 font-sans"
          role="alert"
          aria-live="assertive"
        >
          <h2 className="text-xl font-semibold text-primary">{i18n.t('pageErrorBoundary.title', { defaultValue: 'Something went wrong' })}</h2>
          <p className="max-w-md text-center text-sm text-primary/70">
            {this.props.message ?? i18n.t('pageErrorBoundary.message', { defaultValue: 'An unexpected error occurred. You can try again or refresh the page.' })}
          </p>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={this.handleRetry}
              className="rounded-[16px] bg-primary px-4 py-2 text-sm font-medium text-white hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
            >
              {i18n.t('pageErrorBoundary.tryAgain', { defaultValue: 'Try again' })}
            </button>
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="rounded-[16px] border-2 border-primary bg-transparent px-4 py-2 text-sm font-medium text-primary hover:bg-primary/5 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
            >
              {i18n.t('pageErrorBoundary.refresh', { defaultValue: 'Refresh page' })}
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
